// Every language the team works in: how far each section has got, which sites publish it, adding, removing, and copying wording

import { useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { Fragment } from "react"
import { AlertTriangle, ArrowRight, Check, Plus, Trash2 } from "lucide-react"
import { coverageOf, percent, type Coverage, type LanguageInfo } from "../../languages"
import { isSiteLocale, unsupportedLocaleMessage } from "../../siteLanguages"
import type { RemovalBlocker } from "../../useTeamLanguages"
import { planLocaleCopy, localeCopyBlocker, type SyncPlan, type SyncSource } from "../../localeSync"
import { BUCKET_LABELS, type SectionKey } from "../../localeSyncAdapters"
import { AddLanguage } from "./AddLanguage"
import { CoverageBar } from "./CoverageLine"
import { StatusDot, StatusLegend } from "./StatusDot"

export interface SectionOverview {
  section: SectionKey
  label: string
  sourceLocale: string
  listed: string[] // languages this section offers readers
  hasRegistry: boolean // false for Site Config: each site picks its own
  canEdit: boolean
  sources: SyncSource[] // every translatable record, for coverage and copying
}

export interface SiteSummary {
  id: string
  name: string
  locales: string[]
  defaultLocale?: string
}

export interface LanguagesPanelProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  languages: LanguageInfo[]
  sections: SectionOverview[]
  sites: SiteSummary[]
  workingLocale: string
  onWorkingLocaleChange: (code: string) => void
  onAddLanguage: (code: string) => void
  onAddToSection: (section: SectionKey, code: string) => void
  removalBlockers: (code: string) => RemovalBlocker[]
  onRemoveLanguage: (code: string) => void
  onCopy: (section: SectionKey, plan: SyncPlan, approvedKeys: string[]) => void
}

export function LanguagesPanel(props: LanguagesPanelProps) {
  const [tab, setTab] = useState<"languages" | "copy">("languages")
  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl">
        <SheetHeader>
          <SheetTitle>Languages</SheetTitle>
          <SheetDescription>
            The languages your content is written in, how far each part of the Smart CMS has got, and which sites offer them.
          </SheetDescription>
        </SheetHeader>
        <div className="pt-4">
          <div role="tablist" className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
            {([["languages", "Languages"], ["copy", "Copy wording"]] as const).map(([id, label]) => (
              <button
                key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
                className={cn(
                  "rounded-md px-3 py-1 text-xs font-medium transition-all",
                  tab === id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <ScrollArea className="h-[calc(100vh-10rem)] [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
          <div className="pb-6 pt-3">
            {tab === "languages" ? <LanguagesTab {...props} /> : <CopyTab {...props} />}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

// ── Languages ────────────────────────────────────────────────────────────────

function LanguagesTab({
  languages, sections, sites, workingLocale, onWorkingLocaleChange,
  onAddLanguage, onAddToSection, removalBlockers, onRemoveLanguage,
}: LanguagesPanelProps) {
  // The row whose removal details are open
  const [removing, setRemoving] = useState<string | null>(null)
  const canAdd = sections.some(s => s.hasRegistry && s.canEdit)

  // Coverage for every language × section, computed once per change
  const coverage = useMemo(() => {
    const out = new Map<string, Coverage>()
    for (const s of sections) {
      const records = s.sources.map(src => src.record)
      for (const l of languages) out.set(`${s.section}:${l.code}`, coverageOf(records, l.code, s.sourceLocale))
    }
    return out
  }, [sections, languages])

  const sources = [...new Set(sections.map(s => s.sourceLocale))]
  const nameOf = (code: string) => languages.find(l => l.code === code)?.name ?? code

  return (
    <div className="space-y-4">
      {canAdd && (
        <div className="space-y-1.5">
          <AddLanguage existing={languages.map(l => l.code)} onAdd={onAddLanguage} />
          <p className="text-[11px] text-muted-foreground">
            Added to {sections.filter(s => s.hasRegistry && s.canEdit).map(s => s.label).join(" and ")}. Sites choose which languages they offer in their settings.
          </p>
        </div>
      )}

      {sources.length > 1 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            The sections have different source languages ({sections.map(s => `${s.label}: ${nameOf(s.sourceLocale)}`).join(", ")}).
            Missing text falls back to each section's own source.
          </span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-semibold">Language</th>
                {sections.map(s => <th key={s.section} className="min-w-[120px] px-3 py-2 font-semibold">{s.label}</th>)}
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {languages.map(l => {
                const working = l.code === workingLocale
                const isSource = sections.some(s => s.sourceLocale === l.code)
                const invalid = !isSiteLocale(l.code)
                const publishedBy = sites.filter(site => site.locales.includes(l.code))
                const open = removing === l.code
                return (
                  <Fragment key={l.code}>
                  <tr className={cn("align-top", !open && "border-b last:border-0", working && "bg-primary/5")}>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        className="group flex flex-col items-start text-left"
                        onClick={() => onWorkingLocaleChange(l.code)}
                        title={working ? "The language you're working in" : `Work in ${l.name}`}
                      >
                        <span className="flex items-center gap-1.5 font-medium group-hover:underline">
                          {l.name}
                          {working && <Badge className="h-4 px-1.5 text-[9px]">working</Badge>}
                        </span>
                        <span className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
                          {l.code}
                          {l.direction === "rtl" && <span className="font-sans uppercase">· rtl</span>}
                        </span>
                      </button>
                      {invalid && (
                        <p className="mt-1 flex max-w-[220px] items-start gap-1 text-[10px] text-rose-700 dark:text-rose-400">
                          <AlertTriangle className="mt-px h-3 w-3 shrink-0" /> {unsupportedLocaleMessage(l.code)}
                        </p>
                      )}
                    </td>
                    {sections.map(s => (
                      <td key={s.section} className="px-3 py-2.5">
                        <SectionCell
                          section={s}
                          language={l}
                          coverage={coverage.get(`${s.section}:${l.code}`)!}
                          publishedBy={s.section === "siteConfig" ? publishedBy : undefined}
                          onAdd={() => onAddToSection(s.section, l.code)}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-2.5 text-right">
                      {/* The source language can't go, so it gets no bin at all */}
                      {!isSource && (
                        <Button
                          size="icon" variant="ghost"
                          className={cn("h-7 w-7 text-muted-foreground hover:text-destructive", open && "bg-muted text-foreground")}
                          title={`Remove ${l.name}`}
                          aria-expanded={open}
                          onClick={() => setRemoving(open ? null : l.code)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                  {open && (
                    <tr className={cn("border-b last:border-0", working && "bg-primary/5")}>
                      <td colSpan={sections.length + 2} className="px-3 pb-3">
                        <RemovalDetails
                          language={l}
                          blockers={removalBlockers(l.code)}
                          onRemove={() => { onRemoveLanguage(l.code); setRemoving(null) }}
                          onCancel={() => setRemoving(null)}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <StatusLegend />
      <p className="text-[11px] text-muted-foreground">
        A language can be removed once nothing is written in it and no site offers it. Click the bin to see what's in the way.
      </p>
    </div>
  )
}

// What removing a language would take: either a confirm, or each thing in the way and how to clear it
function RemovalDetails({ language, blockers, onRemove, onCancel }: {
  language: LanguageInfo
  blockers: RemovalBlocker[]
  onRemove: () => void
  onCancel: () => void
}) {
  if (!blockers.length) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
        <span className="flex-1">Remove {language.name}? Nothing is written in it, so no text is lost.</span>
        <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={onRemove}>Remove {language.name}</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
      </div>
    )
  }
  const textInTheWay = blockers.some(b => b.kind === "content")
  return (
    <div className="space-y-2 rounded-lg border bg-muted/40 px-3 py-2 text-xs">
      <p className="font-medium">{language.name} can't be removed yet:</p>
      <ul className="space-y-1.5">
        {blockers.map((b, i) => (
          <li key={i} className="flex flex-wrap items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/60" />
            <span className="min-w-0 flex-1 text-muted-foreground">{b.message}</span>
            {b.fix && (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={b.fix.run}>{b.fix.label}</Button>
            )}
          </li>
        ))}
      </ul>
      {textInTheWay && (
        <p className="text-[11px] text-muted-foreground">
          Text has to be cleared record by record, so nothing is deleted by accident. Work in {language.name} to find it: the trees mark every record that has it.
        </p>
      )}
      <div className="flex justify-end">
        <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={onCancel}>Close</Button>
      </div>
    </div>
  )
}

function SectionCell({ section, language, coverage, publishedBy, onAdd }: {
  section: SectionOverview
  language: LanguageInfo
  coverage: Coverage
  publishedBy?: SiteSummary[]
  onAdd: () => void
}) {
  const isSource = language.code === section.sourceLocale
  const listed = section.listed.includes(language.code)

  if (isSource) {
    return (
      <span className="flex items-center gap-1.5 text-xs">
        <StatusDot status="source" /> Source
        <span className="text-muted-foreground">· {coverage.total}</span>
      </span>
    )
  }

  if (section.hasRegistry && !listed) {
    return section.canEdit ? (
      <Button variant="ghost" size="sm" className="-ml-2 h-6 gap-1 px-2 text-[11px] text-muted-foreground" onClick={onAdd}>
        <Plus className="h-3 w-3" /> Add
      </Button>
    ) : <span className="text-[11px] text-muted-foreground">Not added</span>
  }

  return (
    <div className="space-y-1" title={`${coverage.reviewed} reviewed · ${coverage.unreviewed} need review · ${coverage.missing} missing`}>
      {coverage.total > 0 ? (
        <>
          <div className="flex items-baseline gap-1 text-xs">
            <span className="font-medium tabular-nums">{percent(coverage)}%</span>
            <span className="text-[10px] tabular-nums text-muted-foreground">{coverage.translated}/{coverage.total}</span>
          </div>
          <CoverageBar coverage={coverage} className="max-w-[110px]" />
          {coverage.unreviewed > 0 && (
            <p className="text-[10px] text-amber-700 dark:text-amber-400">{coverage.unreviewed} to review</p>
          )}
        </>
      ) : (
        <span className="text-[11px] text-muted-foreground">Nothing to translate</span>
      )}
      {publishedBy && (
        <p className="text-[10px] text-muted-foreground" title={publishedBy.map(s => s.name).join(", ")}>
          {publishedBy.length
            ? <><Check className="mr-0.5 inline h-3 w-3 text-emerald-600" />Offered by {publishedBy.length === 1 ? publishedBy[0].name : `${publishedBy.length} sites`}</>
            : "No site offers it"}
        </p>
      )}
    </div>
  )
}

// ── Copy wording ─────────────────────────────────────────────────────────────

function CopyTab({ languages, sections, onCopy }: LanguagesPanelProps) {
  const [copySection, setCopySection] = useState<SectionKey>(sections[0]?.section ?? "articles")
  const active = sections.find(s => s.section === copySection)
  const [from, setFrom] = useState(active?.sourceLocale ?? "")
  const [to, setTo] = useState("")
  const [plan, setPlan] = useState<SyncPlan | null>(null)
  const [approved, setApproved] = useState<Set<string>>(new Set())
  const blocker = localeCopyBlocker(from, to)
  const nameOf = (code: string) => languages.find(l => l.code === code)?.name ?? code

  const discard = () => { setPlan(null); setApproved(new Set()) }
  const preview = () => {
    if (!active || blocker) return
    const p = planLocaleCopy(active.sources, from, to)
    setPlan(p)
    // Overwrites start unchecked
    setApproved(new Set(p.entries.filter(e => e.conflict === "new").map(e => e.key)))
  }
  const toggle = (key: string) => setApproved(prev => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  const grouped = useMemo(() => {
    const byBucket = new Map<string, SyncPlan["entries"]>()
    for (const e of plan?.entries ?? []) byBucket.set(e.bucket, [...(byBucket.get(e.bucket) ?? []), e])
    return [...byBucket.entries()]
  }, [plan])
  const overwrites = plan?.entries.filter(e => e.conflict === "overwrite").length ?? 0

  // Copies only go into languages the sites can show
  const languageSelect = (value: string, onChange: (v: string) => void, placeholder: string, exclude?: string, supportedOnly = false) => (
    <Select value={value} onValueChange={v => { onChange(v); discard() }}>
      <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {languages.filter(l => l.code !== exclude && (!supportedOnly || isSiteLocale(l.code))).map(l => (
          <SelectItem key={l.code} value={l.code} className="text-xs">
            {l.name} <span className="font-mono text-[10px] text-muted-foreground">{l.code}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Copies wording verbatim, as a starting point for a translator. It doesn't translate. Copies arrive unreviewed and unpublished, so nothing unread goes live.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <Labelled label="Section">
          <Select value={copySection} onValueChange={v => {
            setCopySection(v as SectionKey)
            setFrom(sections.find(s => s.section === v)?.sourceLocale ?? "")
            discard()
          }}>
            <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sections.map(s => <SelectItem key={s.section} value={s.section} className="text-xs">{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Labelled>
        <Labelled label="From">{languageSelect(from, setFrom, "Source")}</Labelled>
        <ArrowRight className="mb-2 h-4 w-4 text-muted-foreground" />
        <Labelled label="To">{languageSelect(to, setTo, "Choose a language", from, true)}</Labelled>
        <Button size="sm" className="h-8 text-xs" disabled={!!blocker} onClick={preview}>Preview</Button>
      </div>

      {plan && (
        <div className="overflow-hidden rounded-xl border">
          <div className="grid grid-cols-3 gap-px bg-border">
            <Stat label="New" value={plan.entries.length - overwrites} tone="positive" />
            <Stat label="Would replace" value={overwrites} tone={overwrites ? "warning" : "neutral"} />
            <Stat label={`No ${nameOf(plan.from)} text`} value={plan.skipped} tone="neutral" />
          </div>
          {plan.entries.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">Nothing to copy: no record has {nameOf(plan.from)} text.</p>
          ) : (
            <div className="px-4 py-3">
              <p className="mb-2 text-xs text-muted-foreground">
                {approved.size} of {plan.entries.length} selected
                {overwrites > 0 && ` · ${overwrites} would replace existing ${nameOf(plan.to)} text, so they start unchecked`}
              </p>
              <div className="max-h-80 space-y-3 overflow-y-auto pr-2">
                {grouped.map(([bucket, entries]) => (
                  <div key={bucket}>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {BUCKET_LABELS[bucket] ?? bucket} ({entries.length})
                    </p>
                    {entries.map(e => (
                      <label key={e.key} className={cn(
                        "flex cursor-pointer items-start gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/60",
                        e.conflict === "overwrite" && "bg-amber-50 dark:bg-amber-950/20",
                      )}>
                        <Checkbox checked={approved.has(e.key)} onCheckedChange={() => toggle(e.key)} className="mt-0.5" />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate font-medium">{e.name}</span>
                            {e.conflict === "overwrite" && (
                              <Badge variant="outline" className="shrink-0 px-1 py-0 text-[10px] text-amber-700 dark:text-amber-400">replaces</Badge>
                            )}
                          </span>
                          {e.preview && <span className="block truncate text-[11px] text-muted-foreground">{e.preview}</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button size="sm" className="h-8 text-xs" disabled={approved.size === 0}
                  onClick={() => { onCopy(copySection, plan, [...approved]); discard() }}>
                  Copy {approved.size} into {nameOf(plan.to)}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={discard}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <span className="block text-[11px] font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone: "positive" | "warning" | "neutral" }) {
  return (
    <div className="bg-background px-4 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn(
        "text-lg font-semibold tabular-nums",
        tone === "positive" && value > 0 && "text-emerald-600",
        tone === "warning" && value > 0 && "text-amber-600",
      )}>{value}</p>
    </div>
  )
}
